import React, {useEffect, useState} from 'react';
import {useDispatch} from 'react-redux';
import '../PartnerReferalRewardModal/PartnerReferalRewardModal.css';
import PageButton from '../../elements/PageButton/PageButton';
import {API_BASE} from '../../../utils/apiConfig';
import {applyLoginPromoRewardsClaim, setAvailableFactions} from '../../../redux/actions';
import {getPartnerRewardDisplayProps} from '../../../utils/partner/getPartnerRewardDisplayProps';
import {MENU_SOUND_MANAGER} from '../../../scenes/MenuSoundManager';
import {INVENTORY_ITEMS} from '../../../constants/inventoryItems';

const PAGE_ID = 'LoginPromoRewardModal';

/**
 * Модалка награды по акции за вход (главное меню). Построена на классах
 * модалки реферальной награды (PartnerReferalRewardModal), но без ссылки на
 * профиль партнера — у акции нет «пригласившего», вместо ника показывается
 * название акции.
 *
 * Приоритет ниже реферальных модалок: открывается только после того, как обе
 * они закрыты (гейт — в MainMenu). Офер приходит уже отфильтрованным: сервер
 * отдает только активную акцию, награду по которой этот игрок еще не забирал.
 */
function LoginPromoRewardModal({onClose, onUnavailable}) {
  const dispatch = useDispatch();
  const [visible, setVisible] = useState(false);
  const [offer, setOffer] = useState(null);
  const [claiming, setClaiming] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchOffer = async () => {
      try {
        const res = await fetch(`${API_BASE}/login-promo/offer`, {credentials: 'include'});
        const data = await res.json().catch(() => ({}));
        if (!res.ok) {
          throw new Error(data.error || 'Акция недоступна');
        }
        setOffer(data);
        setTimeout(() => setVisible(true), 50);
      } catch (e) {
        // Активной акции нет (обычный случай) — модалка не показывается,
        // меню продолжает свой сценарий
        onUnavailable?.();
      }
    };
    fetchOffer();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleClose = () => {
    setVisible(false);
    setTimeout(() => {
      onClose?.();
    }, 300);
  };

  const handleClaimClick = async () => {
    if (claiming) return;
    setClaiming(true);
    setError(null);
    try {
      const res = await fetch(`${API_BASE}/login-promo/claim`, {
        method: 'POST',
        credentials: 'include',
        headers: {'Content-Type': 'application/json'},
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        throw new Error(data.error || 'Не удалось получить награды');
      }

      dispatch(applyLoginPromoRewardsClaim({
        promoId: data.promoId,
        numens: data.numens,
        crystals: data.crystals,
        premiumExpiresAt: data.premiumExpiresAt,
        hasNewItems: data.hasNewItems,
      }));

      // Фракция из наград не попадает в стор сама: список доступных фракций
      // грузится один раз при авторизации, а «Моя коллекция» берет фракции
      // именно оттуда (та же причина, что и у реферальных наград)
      const hasFactionReward = (offer?.rewards || []).some(
        (reward) => reward.itemType === INVENTORY_ITEMS.FACTION
      );
      if (hasFactionReward) {
        try {
          const factionsRes = await fetch(`${API_BASE}/available-factions`, {credentials: 'include'});
          const factionsData = await factionsRes.json().catch(() => ({}));
          if (factionsRes.ok) {
            dispatch(setAvailableFactions(factionsData.factions || []));
          }
        } catch (e) {
          console.error('Refresh available factions after login promo claim error', e);
        }
      }

      MENU_SOUND_MANAGER.playCoins(data.numens);
      MENU_SOUND_MANAGER.playChestOpen();
      handleClose();
    } catch (e) {
      console.error('Claim login promo rewards error', e);
      setError(e.message || 'Не удалось получить награды');
    } finally {
      setClaiming(false);
    }
  };

  const handleOverlayClick = (event) => {
    if (event.target === event.currentTarget && !claiming) {
      handleClose();
    }
  };

  if (!offer) return null;

  return (
    <div
      className={`partner-referal-reward-overlay ${visible ? 'partner-referal-reward-overlay_visible' : ''}`}
      onClick={handleOverlayClick}
      role="dialog"
      aria-modal="true"
    >
      <div className={`partner-referal-reward-modal ${visible ? 'partner-referal-reward-modal_visible' : ''}`}>
        <h2 className="partner-referal-reward-title">Поздравляем!</h2>

        <p className="partner-referal-reward-description">
          Вы получили награды по акции{' '}
          <span className="partner-referal-reward-partner-name">{offer.name}</span>
        </p>

        <div className="partner-referal-reward-list">
          {offer.rewards.map((reward, index) => {
            const display = getPartnerRewardDisplayProps(reward);
            let descriptionText = '';
            if (display.itemType === INVENTORY_ITEMS.CRYSTALS) {
              descriptionText = 'кристаллов';
            } else if (display.itemType === INVENTORY_ITEMS.NUMENS) {
              descriptionText = 'нуменов';
            }
            return (
              <div
                key={index}
                className={`partner-referal-reward-item ${display.colorClass} partner-form-reward-preview_type-${display.itemType}`}
              >
                {display.iconSrc && (
                  <img className="partner-referal-reward-item-icon" src={display.iconSrc} alt=""/>
                )}
                <span className="partner-referal-reward-item-label">{display.fullLabel + ' ' + descriptionText}</span>
              </div>
            );
          })}
        </div>

        {error && <p className="partner-referal-reward-error">{error}</p>}

        <div className="main-menu-buttons partner-referal-reward-buttons">
          <PageButton
            onClick={handleClaimClick}
            pageId={PAGE_ID}
            buttonId="claim"
            disabled={claiming}
          >
            {claiming ? 'Получение...' : 'Забрать дар'}
          </PageButton>
        </div>
      </div>
    </div>
  );
}

export default LoginPromoRewardModal;
